import { useState, useEffect, useRef } from 'react';
import { useCelebration, CelebrationTheme } from '@/context/CelebrationContext';

/* ── particle types ── */
interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  speed: number;
  drift: number;
  opacity: number;
  char?: string;
  rotation: number;
  rotSpeed: number;
}

const THEME_CHARS: Record<CelebrationTheme, string[]> = {
  fireworks: ['★', '✦', '✧', '✶', '✷', '✸'],
  confetti:  ['■', '▲', '●', '◆', '▬', '▮'],
  snowflakes:['❄', '❅', '❆', '✻', '✼', '❋'],
  hearts:    ['♥', '♡', '❤', '💕', '❣'],
  stars:     ['★', '☆', '✦', '✧', '⭐'],
  waves:     ['〰', '≈', '∿', '〜', '～'],
};

const THEME_COLORS: Record<CelebrationTheme, string[]> = {
  fireworks: ['#F97316', '#FBBF24', '#EF4444', '#A855F7', '#3B82F6', '#10B981'],
  confetti:  ['#F97316', '#EC4899', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'],
  snowflakes:['#BAE6FD', '#E0F2FE', '#F0F9FF', '#DBEAFE', '#C7D2FE'],
  hearts:    ['#F43F5E', '#FB7185', '#FDA4AF', '#FCA5A5', '#FECDD3'],
  stars:     ['#FBBF24', '#FCD34D', '#FDE68A', '#F97316', '#FDBA74'],
  waves:     ['#06B6D4', '#22D3EE', '#67E8F9', '#A5F3FC', '#0EA5E9'],
};

function createParticle(id: number, theme: CelebrationTheme, accentColor: string): Particle {
  const chars = THEME_CHARS[theme];
  const colors = THEME_COLORS[theme];
  return {
    id,
    x: Math.random() * 100,
    y: -10,
    size: 8 + Math.random() * 14,
    color: Math.random() > 0.3 ? colors[Math.floor(Math.random() * colors.length)] : accentColor,
    speed: 0.3 + Math.random() * 0.7,
    drift: (Math.random() - 0.5) * 0.4,
    opacity: 0.7 + Math.random() * 0.3,
    char: chars[Math.floor(Math.random() * chars.length)],
    rotation: Math.random() * 360,
    rotSpeed: (Math.random() - 0.5) * 3,
  };
}

function ParticleCanvas({ theme, accentColor }: { theme: CelebrationTheme; accentColor: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const frameRef = useRef<number>(0);
  const counterRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let pid = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      counterRef.current++;
      if (counterRef.current % 4 === 0 && particlesRef.current.length < 60) {
        particlesRef.current.push(createParticle(pid++, theme, accentColor));
      }
      particlesRef.current = particlesRef.current.filter((p) => {
        p.y += p.speed;
        p.x += p.drift;
        p.rotation += p.rotSpeed;
        if (p.y > 110) return false;
        ctx.save();
        ctx.globalAlpha = p.opacity * (1 - p.y / 110);
        ctx.font = `${p.size}px serif`;
        ctx.fillStyle = p.color;
        ctx.translate((p.x / 100) * canvas.width, (p.y / 100) * canvas.height);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillText(p.char || '★', 0, 0);
        ctx.restore();
        return true;
      });
      frameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [theme, accentColor]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

/* ── main banner ── */
export default function CelebrationBanner() {
  const { activeDay } = useCelebration();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (activeDay) {
      setDismissed(false);
      timeoutId = window.setTimeout(() => setVisible(true), 100);
    } else {
      setVisible(false);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [activeDay]);

  if (!activeDay || dismissed) return null;

  const accent = activeDay.themeColor || '#F97316';

  return (
    <div
      className={`relative overflow-hidden transition-all duration-700 ${visible ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}
      style={{ background: `linear-gradient(135deg, ${accent}22 0%, ${accent}11 50%, ${accent}22 100%)`, borderBottom: `2px solid ${accent}44` }}
    >
      {/* Particle canvas */}
      <ParticleCanvas theme={activeDay.theme} accentColor={accent} />

      {/* Content */}
      <div className="relative z-10 flex items-center justify-between px-6 py-3 gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Emoji pulse */}
          <div
            className="text-3xl shrink-0 animate-bounce"
            style={{ animationDuration: '1.5s' }}
          >
            {activeDay.emoji}
          </div>
          <div className="min-w-0">
            <p
              className="font-black text-base md:text-lg leading-tight truncate"
              style={{ color: accent }}
            >
              {activeDay.message}
            </p>
            {activeDay.subMessage && (
              <p className="text-xs md:text-sm text-gray-600 dark:text-white/60 mt-0.5 truncate">
                {activeDay.subMessage}
              </p>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 shrink-0">
          <a
            href="/products"
            className="hidden sm:flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap transition-all hover:opacity-90"
            style={{ background: accent, color: '#fff' }}
          >
            Explore Products <i className="ri-arrow-right-line"></i>
          </a>
          {activeDay.dismissible && (
            <button
              onClick={() => setDismissed(true)}
              className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-black/10 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
